/**
 * Productos / softwares propios de Coralan Multiservice. Separados de los servicios porque
 * tienen otro ciclo de venta. Alimentan la sección "Nuestros softwares" de la portada
 * (<SoftwareShowcase>), la página /productos, el sitemap/prerender y el schema SoftwareApplication.
 *
 * Reglas comerciales:
 *  - Precios en USD, salvo los productos de facturación electrónica (ERP con FE, POS con FE) → DOP.
 *  - Donde no hay precio confirmado: `from: null` + `// TODO: precio pendiente`. NO inventar cifras.
 *  - `links` con null hasta tener las URLs reales (sitio, Google Play, App Store).
 *
 * Campos de mensaje (se muestran en la portada, /productos y sirven para Google Business e Instagram):
 *  - audience: para quién es (sectores/tipo de negocio).
 *  - pitch: frase de venta de una línea (usar la MISMA en la web, la ficha de Google y redes).
 *  - problems: dolores concretos que resuelve, en el lenguaje del cliente (3–5; se muestran en "¿Qué resuelve?").
 *  - highlights: cómo lo resuelve (funcionalidades). Máx. 4 visibles en la portada.
 *  Editar aquí → `npm run build` → subir build/. Ver docs/seo/como-actualizar.md.
 *
 * Medios para el showcase (`media`): pon el archivo en public/assets/custom/products/ y referencia:
 *  - screenshot: PNG/JPG 1600×1000 (captura real del software) → se muestra dentro del mockup de laptop.
 *  - video: MP4 (H.264, sin audio, 15–30 s, ≤ 3 MB, 1600×1000) → se reproduce en bucle silencioso dentro del mockup.
 *  - poster: JPG del primer frame del video (se usa mientras carga y en el prerender).
 *  Si todo es null se muestra una interfaz de ejemplo dibujada en CSS con el nombre del producto.
 *
 * Archivo .mjs sin JSX, importable desde Node.
 */
export const PRODUCTS = [
    {
        slug: 'agendamax',
        name: 'AgendaMax.do',
        tag: 'Agenda + POS + comisiones',
        tagline: 'Agenda, punto de venta y comisiones para salones, barberías, spas y estudios de uñas.',
        audience: 'Salones de belleza, barberías, spas, estudios de uñas y centros de estética.',
        pitch: 'Deje de contestar WhatsApp para agendar: sus clientes reservan solos y usted cobra y factura desde el mismo sistema.',
        problems: [
            'Citas por WhatsApp que hay que confirmar una por una',
            'Inasistencias y sillas vacías por citas olvidadas',
            'Comisiones y nómina de estilistas calculadas en Excel',
            'Sin historial del cliente: qué servicio, qué producto, cuándo volvió',
        ],
        category: 'BusinessApplication',
        markets: ['RD', 'LATAM'],
        currency: 'USD',
        pricing: { public: false, from: null, period: 'mes' }, // TODO: precio pendiente
        links: { site: 'https://agendamax.do', googlePlay: null, appStore: null }, // TODO: URLs de tiendas
        icon: 'fas fa-calendar-check',
        accent: '#7C3AED',
        media: { screenshot: null, video: null, poster: null }, // TODO: captura/video real
        highlights: [
            'Reservas en línea y app móvil para el cliente final',
            'Punto de venta con facturación electrónica e-CF integrada',
            'Nómina y comisiones por colaborador',
            'Recordatorios automáticos que reducen inasistencias',
        ],
    },
    {
        slug: 'erp-fe',
        name: 'ERP con Facturación Electrónica',
        tag: 'ERP + e-CF',
        tagline: 'Contabilidad, ventas, compras, inventario y comprobantes fiscales electrónicos (e-CF) en un solo sistema.',
        audience: 'Pymes de comercio, distribución y servicios obligadas a emitir e-CF.',
        pitch: 'Cumpla con la DGII sin cambiar el sistema que ya usa, y con la contabilidad al día.',
        problems: [
            'El plazo de la DGII (15 de noviembre de 2026) y el riesgo de sanciones',
            'Facturar en un programa y llevar la contabilidad en otro',
            'Reportes 606/607/608 armados a mano cada mes',
            'No saber cuánto le deben ni conciliar el banco a tiempo',
            'Varias sucursales sin una visión única',
        ],
        category: 'BusinessApplication',
        markets: ['RD'],
        currency: 'DOP', // producto de FE → pesos
        pricing: { public: true, from: 990, period: 'mes' }, // planes de /facturacion-electronica (desde RD$990)
        links: { site: '/facturacion-electronica', googlePlay: null, appStore: null },
        icon: 'fas fa-file-invoice-dollar',
        accent: '#E54D42',
        media: { screenshot: null, video: null, poster: null }, // TODO: captura/video real
        highlights: [
            'Emisión de e-CF validados por la DGII y reportes 606, 607 y 608',
            'Contabilidad integrada y conciliación bancaria',
            'CRM de clientes con validación de RNC y cuentas por cobrar',
            'Multi-sucursal y usuarios ilimitados en el plan Empresarial',
        ],
    },
    {
        slug: 'internobis',
        name: 'Internobis',
        tag: 'CRM inmobiliario',
        tagline: 'CRM inmobiliario para gestión de propiedades, prospectos y cartera de agentes.',
        audience: 'Inmobiliarias, agentes y desarrolladores de proyectos.',
        pitch: 'Todas sus propiedades, prospectos y agentes en un solo lugar; ningún interesado se queda sin seguimiento.',
        problems: [
            'Propiedades repartidas en fotos del celular y grupos de WhatsApp',
            'Prospectos que se enfrían porque nadie les dio seguimiento',
            'No saber qué agente mostró o cerró cada propiedad',
        ], // TODO: confirmar con Alan
        category: 'BusinessApplication',
        markets: ['RD', 'LATAM', 'USA'],
        currency: 'USD',
        pricing: { public: false, from: null, period: 'mes' }, // TODO: precio pendiente
        links: { site: null, googlePlay: null, appStore: null }, // TODO
        icon: 'fas fa-building',
        accent: '#0EA5E9',
        media: { screenshot: null, video: null, poster: null }, // TODO
        highlights: [
            // TODO: completar con Alan (funcionalidades reales de Internobis)
            'Inventario de propiedades con fotos, precios y estado',
            'Seguimiento de prospectos y visitas por agente',
        ],
    },
    {
        slug: 'colegiomax',
        name: 'ColegioMax con FE',
        tag: 'Gestión escolar + e-CF',
        tagline: 'Gestión escolar: matrícula, calificaciones, cobros con facturación electrónica y comunicación con padres.',
        audience: 'Colegios, centros educativos y academias.',
        pitch: 'Cobre a tiempo, informe a los padres y cumpla con la DGII desde un mismo sistema.',
        problems: [
            'Matrícula, notas y boletines en Excel',
            'Padres que no se enteran de circulares, calificaciones ni pagos',
            'Perseguir la colegiatura y la mora cada mes',
            'Emitir un comprobante fiscal por cada pago',
        ], // TODO: confirmar con Alan
        category: 'EducationalApplication',
        markets: ['RD', 'LATAM'],
        currency: 'USD',
        pricing: { public: false, from: null, period: 'mes' }, // TODO: precio pendiente
        links: { site: null, googlePlay: null, appStore: null }, // TODO
        icon: 'fas fa-graduation-cap',
        accent: '#16A34A',
        media: { screenshot: null, video: null, poster: null }, // TODO
        highlights: [
            // TODO: completar con Alan
            'Matrícula, calificaciones y boletines',
            'Cobros de colegiatura con e-CF integrado',
            'Comunicación con padres y tutores',
        ],
    },
    {
        slug: 'pos-fe',
        name: 'POS con Facturación Electrónica',
        tag: 'Punto de venta + e-CF',
        tagline: 'Punto de venta para comercios y restaurantes: cada venta genera su comprobante fiscal electrónico automáticamente.',
        audience: 'Tiendas, farmacias, colmados grandes, restaurantes y bares.',
        pitch: 'Cobre y facture electrónicamente en un solo paso, sin frenar la fila.',
        problems: [
            'Cada venta debe generar un e-CF y hacerlo a mano no escala',
            'Inventario que no cuadra y cierres de caja por turno a mano',
            'El cliente pide la factura por WhatsApp',
        ],
        category: 'BusinessApplication',
        markets: ['RD'],
        currency: 'DOP',
        pricing: { public: false, from: null, period: 'mes' }, // TODO: precio pendiente
        links: { site: '/facturacion-electronica', googlePlay: null, appStore: null },
        icon: 'fas fa-cash-register',
        accent: '#F59E0B',
        media: { screenshot: null, video: null, poster: null }, // TODO
        highlights: [
            'e-CF automático en cada venta (31, 32 y notas de crédito)',
            'Inventario, cajas y turnos',
            'Impresión con QR y envío por WhatsApp/correo',
        ],
    },
];

export const getProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);
export const productsByMarket = (market = 'RD') => PRODUCTS.filter((p) => (p.markets ?? ['RD']).includes(market));
